import { useRouter } from 'next/router'
import { FC, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import {
  DropzoneErrors,
  DropzoneProvider,
  DropzoneTextContainer,
  TypeStyle,
  Upload,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { useAuthentication } from '../../../../../app/_providers/AuthenticationProvider.client'
import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useFixturesContext } from '../../../../../brunswick/containers/fixtures'
import { EmbroideryService } from '../../../../../containers/EmbroideryService'
import { trackEvent } from '../../../../../lib/analytics'
import { intlPrettyCurrency } from '../../../../../lib/utils'
import { FormBody } from '../../shared-form-styles'
import { ErrorMessage } from '../../shared-form-styles'
import { PriceLabel } from '../../shared-form-styles'
import * as Name from '../../TextEmbroideryForm/FormBody/Name'
import { LogoFormContainer } from '../LogoFormContainer'

import { LogoColorSelector } from './LogoColorSelector'
import * as Note from './Note'
import * as SignIn from './SignIn'
import * as Styles from './styles'
import { TosCheckbox } from './TosCheckbox'

const maxFilesSize = 3145728

export const EmbroideryLogoSetup: FC = () => {
  const { logoSetupForm } = LogoFormContainer.useContainer()
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: fixtures },
      },
    },
  } = useTranslation()

  const { errors, textLine1, textLine2, mobileLine } = fixtures.logoUploadDropzone
  const { fileInvalidType, fileTooLarge, fileTooSmall, tooManyFiles, defaultError } = errors
  const { logoLearnMorelink: logoLearnMoreLink, logoDigitizationWarning } = useFixturesContext()
  const { region, locale } = useLocalization()
  const { embroideryVariantsByKind } = EmbroideryService.useContainer()
  const { isLoggedIn } = useAuthentication()
  const isSmallViewport = useIsViewport(ViewportSizes['small'])
  const [showNote, setShowNote] = useState(false)

  const router = useRouter()
  const currentUrl = router.asPath

  const { color } = useTheme()

  const onFileDropped = (files: File[]) => {
    const firstFile = files[0]
    if (firstFile) {
      if (firstFile.size < maxFilesSize) {
        logoSetupForm.setFieldValue('file', firstFile)
      } else {
        logoSetupForm.setFieldError('file', fixtures.fileSizeError)
      }
    }
  }

  const onRemoveFile = () => {
    logoSetupForm.setFieldValue('file', undefined)
  }

  const setupPrice = intlPrettyCurrency({
    unit: 'cents',
    value: embroideryVariantsByKind?.LOGO_SETUP?.price,
    region: region,
    locale: locale,
    truncateTrailingZeros: true,
  })

  const imgSrc = useMemo(() => {
    if (!logoSetupForm.values.file) return
    return URL.createObjectURL(logoSetupForm.values.file)
  }, [logoSetupForm.values.file])

  const { uploadSpecs } = fixtures

  const imageFormatsMatch = uploadSpecs.match(/PNG.*?EPS/) ?? ''

  return (
    <FormBody onSubmit={logoSetupForm.handleSubmit}>
      <Styles.Label>
        <Styles.MicroCaps>{fixtures.title}</Styles.MicroCaps>
        <PriceLabel>{setupPrice}</PriceLabel>
      </Styles.Label>
      <Styles.Text>
        {logoDigitizationWarning + ` `}
        <Styles.LearnMoreLink
          target='_blank'
          href={logoLearnMoreLink ?? '#'}
          aria-label={fixtures.ariaLabelLearnMoreLink}
        >
          {fixtures.learnMore}
        </Styles.LearnMoreLink>
      </Styles.Text>

      <DropzoneProvider>
        <Styles.DropzoneContainer
          errorStrings={{
            fileInvalidType,
            fileTooLarge,
            fileTooSmall,
            tooManyFiles,
            defaultError,
          }}
          onFilesAdded={onFileDropped}
          maxFileNum={1}
        >
          <DropzoneTextContainer>
            <Upload color={color.stroke.neutral.text.primary.on.background} />
            {isSmallViewport ? (
              <TypeStyle.BodyMedium>{mobileLine}</TypeStyle.BodyMedium>
            ) : (
              <>
                <TypeStyle.BodyMedium>{textLine1}</TypeStyle.BodyMedium>
                <TypeStyle.BodyMedium>{textLine2}</TypeStyle.BodyMedium>
              </>
            )}
          </DropzoneTextContainer>
          <Styles.DropzoneImagePreview onRemoveFiles={onRemoveFile} />
          <DropzoneErrors />
        </Styles.DropzoneContainer>
        <Styles.FileNames />
      </DropzoneProvider>

      {imgSrc ? (
        <>
          <ErrorMessage inset role='alert'>
            {logoSetupForm.errors['file']}
          </ErrorMessage>
          <Styles.StyledNameWrapper label='name-logo' title={fixtures.logoName} required>
            <Name.Input
              ariaLabelledby='name-logo'
              name='name'
              form={logoSetupForm}
              maxCharacterCount={50}
              placeholder={fixtures.logoNamePlaceholder}
            />
          </Styles.StyledNameWrapper>
          {showNote ? (
            <Note.Container>
              <Note.Header>
                <h2>{fixtures.noteLabel}</h2>
                <Note.CloseIcon onClick={() => setShowNote(false)} />
              </Note.Header>
              <Note.TextArea
                {...logoSetupForm.getFieldProps({
                  name: 'notes',
                  placeholder: 'Placeholder',
                  maxLength: 500,
                })}
              />
            </Note.Container>
          ) : (
            <Note.Pill type='button' onClick={() => setShowNote(!showNote)}>
              <Note.PencilIcon />
              {fixtures.openNoteLabel}
            </Note.Pill>
          )}

          <Styles.StyledHr />
          <LogoColorSelector form={logoSetupForm} />
          <Styles.StyledHr />

          <TosCheckbox form={logoSetupForm} />

          <Styles.Button
            type='submit'
            {...trackEvent({
              category: 'embroidery',
              action: 'next logo customization',
            })}
          >
            {fixtures.nextCta}
          </Styles.Button>
          {isSmallViewport ? (
            <ErrorMessage inset role='alert'>
              {logoSetupForm.errors['digitization_setting']}
            </ErrorMessage>
          ) : null}
        </>
      ) : (
        <>
          <ErrorMessage inset role='alert'>
            {logoSetupForm.errors['digitization_setting']}
          </ErrorMessage>
          <Styles.HeadlineMicro>
            {imageFormatsMatch ? (
              <>
                {uploadSpecs.slice(0, imageFormatsMatch.index)}
                <strong>{imageFormatsMatch[0]}</strong>
                {uploadSpecs.slice((imageFormatsMatch.index ?? 0) + imageFormatsMatch[0].length)}
              </>
            ) : (
              uploadSpecs
            )}
          </Styles.HeadlineMicro>
        </>
      )}

      {!isLoggedIn && (
        <>
          <SignIn.LoginAlert icon={<></>}>
            <SignIn.LoginMessage>
              {fixtures.existingLogoLogin.ifExistingLogo}
              <SignIn.LoginLink
                href={`/account/login?redirect_url=${encodeURIComponent(currentUrl)}`}
              >
                {fixtures.existingLogoLogin.loginToAccessIt}
              </SignIn.LoginLink>
              {fixtures.existingLogoLogin.existingLogoEndChar}
            </SignIn.LoginMessage>
          </SignIn.LoginAlert>
        </>
      )}
    </FormBody>
  )
}
